import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSessionState } from '../../hooks/useSessionState';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Search,
  Plus,
  Send,
  CheckCircle,
  Package,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  FileText,
  Edit,
  Trash2,
  Save,
  X,
  RefreshCw,
  Printer,
  Download,
  Mail,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  FilterX,
} from 'lucide-react';
import { api } from '../../core/apiClient';
import apiClient from '../../core/apiClient';
import { formatCurrency, formatDate } from '../../lib/utils';
import { SHIPPING_METHODS } from './constants';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

interface Supplier {
  id: string;
  code: string;
  name: string;
  businessName: string;
  address?: string;
  city?: string;
  postalCode?: string;
  nif?: string;
  phone?: string;
  email?: string;
  paymentTerms?: string;
  bankAccount?: string;
}

interface PurchaseOrder {
  orderId: string;
  orderNumber: string;
  otNumber?: string;
  supplierName: string;
  projectNumber?: string;
  status: 'DRAFT' | 'SENT' | 'PARTIALLY_RECEIVED' | 'RECEIVED' | 'CANCELLED';
  orderDate: string;
  expectedDeliveryDate?: string;
  subtotal: number;
  taxAmount: number;
  total: number;
  currency: string;
  linesCount: number;
  receivedLinesCount: number;
  approvedAt?: string | null;
  deliveryTerm?: string | null;
}

interface EditableOrder {
  id?: string;
  orderNumber?: string;
  otNumber?: string;
  supplierId?: string;
  orderDate?: string;
  expectedDeliveryDate?: string;
  isServed?: boolean;
  persona?: string;
  shippingMethod?: string;
  deliveryTerm?: string;
  deliveryLocation?: string;
  paymentMethod?: string;
  observations?: string;
  contactName?: string;
  contactPhone?: string;
  subtotal?: number;
  taxRate?: number;
  taxAmount?: number;
  total?: number;
  totalWeight?: number;
  currency?: string;
  status?: 'DRAFT' | 'SENT' | 'PARTIALLY_RECEIVED' | 'RECEIVED' | 'CANCELLED';
  lines?: PurchaseOrderLine[];
}

interface PurchaseOrderLine {
  id?: string;
  lineNumber: number;
  element?: number;
  quantity: number;
  unitType: string;
  description: string;
  supplierCode?: string;
  purchasePrice: number;
  weightKg: number;
  unitPrice: number;
  discountPercent: number;
  lineTotal: number;
  materialId?: string;
}

// Arrodoneix a `decimals` decimals — evita que errors de coma flotant en
// cadena (ex: 12.339999999999999) es mostrin en editar la cel·la (els inputs
// type="number" mostren el valor complet, no el format, quan tenen el focus).
const round = (v: number, decimals = 2): number => {
  const factor = 10 ** decimals;
  return Math.round((Number(v) || 0) * factor) / factor;
};

const statusConfig: Record<string, { label: string; color: string }> = {
  DRAFT:              { label: 'Esborrany',         color: 'bg-gray-100 text-gray-800'   },
  SENT:               { label: 'Enviada',           color: 'bg-indigo-100 text-indigo-800' },
  PARTIALLY_RECEIVED: { label: 'Rebuda Parcialment',color: 'bg-orange-100 text-orange-800' },
  RECEIVED:           { label: 'Rebuda',            color: 'bg-green-100 text-green-800'  },
  CANCELLED:          { label: 'Cancel·lada',       color: 'bg-red-100 text-red-800'     },
};

const ACTIVE_STATUSES = ['DRAFT', 'SENT', 'PARTIALLY_RECEIVED'];

const emptyLine: PurchaseOrderLine = {
  lineNumber: 1,
  element: 0,
  quantity: 1,
  unitType: 'Ut',
  description: '',
  supplierCode: '',
  purchasePrice: 0,
  weightKg: 0,
  unitPrice: 0,
  discountPercent: 0,
  lineTotal: 0,
};

const emptyOrder: Partial<EditableOrder> = {
  orderNumber: '',
  otNumber: '',
  supplierId: '',
  orderDate: new Date().toISOString().split('T')[0],
  isServed: false,
  persona: '',
  shippingMethod: '',
  deliveryTerm: '',
  deliveryLocation: '',
  paymentMethod: '',
  observations: '',
  contactName: '',
  contactPhone: '',
  subtotal: 0,
  taxRate: 21,
  taxAmount: 0,
  total: 0,
  totalWeight: 0,
  status: 'DRAFT',
  lines: [],
};

export default function PurchaseOrders({ filterPending = false }: { filterPending?: boolean }) {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch]             = useSessionState('purchases:search', '');
  const [statusFilter, setStatusFilter] = useSessionState<string>('purchases:statusFilter', 'ACTIVE');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Partial<EditableOrder> | null>(null);
  const [lines, setLines] = useState<PurchaseOrderLine[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [saving, setSaving] = useState(false);

  const [actionConfirm, setActionConfirm] = useState<{
    order: PurchaseOrder;
    action: 'submit' | 'cancel' | 'delete';
  } | null>(null);

  type SortField = 'orderNumber' | 'supplierName' | 'projectNumber' | 'orderDate' | 'subtotal' | 'expectedDeliveryDate' | 'status';
  const [sortField, setSortField] = useState<SortField>('orderNumber');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronsUpDown className="h-3 w-3 ml-1 text-gray-400" />;
    return sortDir === 'asc'
      ? <ChevronUp className="h-3 w-3 ml-1 text-blue-600" />
      : <ChevronDown className="h-3 w-3 ml-1 text-blue-600" />;
  };

  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      switch (sortField) {
        case 'orderNumber':      av = a.orderNumber ?? '';       bv = b.orderNumber ?? '';       break;
        case 'supplierName':     av = a.supplierName ?? '';      bv = b.supplierName ?? '';      break;
        case 'projectNumber':    av = a.projectNumber ?? '';     bv = b.projectNumber ?? '';     break;
        case 'orderDate':        av = a.orderDate ?? '';         bv = b.orderDate ?? '';         break;
        case 'subtotal':         av = a.subtotal ?? 0;           bv = b.subtotal ?? 0;           break;
        case 'expectedDeliveryDate': av = a.expectedDeliveryDate ?? ''; bv = b.expectedDeliveryDate ?? ''; break;
        case 'status':           av = a.status ?? '';            bv = b.status ?? '';            break;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [orders, sortField, sortDir]);

  // Helper: Descargar PDF con blob
  const downloadPDF = async (orderId: string, orderNumber: string) => {
    try {
      const response = await apiClient.get(`/purchase-orders/${orderId}/pdf`, {
        responseType: 'blob',
      });
      
      // Verificar que response.data es un Blob
      if (!(response.data instanceof Blob)) {
        throw new Error(`Expected Blob, got ${typeof response.data}`);
      }
      
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comanda-${orderNumber}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[v0] Error descargando PDF:', err);
      setError('Error en descarregar el PDF');
    }
  };

  // Helper: Abrir PDF en pestaña nueva
  const printPDF = async (orderId: string) => {
    try {
      const response = await apiClient.get(`/purchase-orders/${orderId}/pdf`, {
        responseType: 'blob',
      });
      
      // Verificar que response.data es un Blob
      if (!(response.data instanceof Blob)) {
        throw new Error(`Expected Blob, got ${typeof response.data}`);
      }
      
      const url = URL.createObjectURL(response.data);
      window.open(url, '_blank');
      // Revoke after a short delay to allow browser to download it
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error('[v0] Error abriendo PDF:', err);
      setError('Error en obrir el PDF');
    }
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all' && statusFilter !== 'ACTIVE') params.append('status', statusFilter);

      const response = await api.get<{ success: boolean; data: PurchaseOrder[] }>(`/purchase-orders?${params}`);
      const raw = Array.isArray(response.data) ? response.data : [];
      const data = statusFilter === 'ACTIVE'
        ? raw.filter((o: PurchaseOrder) => ACTIVE_STATUSES.includes(o.status))
        : raw;
      setOrders(data);
      setTotal(Array.isArray(data) ? data.length : 0);
      setTotalPages(Array.isArray(data) ? Math.ceil(data.length / limit) : 1);
    } catch (err) {
      console.error('[v0] Error fetching purchase orders:', err);
      setError('Error en carregar les comandes de compra');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    setStatusFilter(filterPending ? 'SENT' : 'ACTIVE');
  }, [filterPending]);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get<{ success: boolean; data: Supplier[] }>('/suppliers?limit=3000');
      setSuppliers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('[v0] Error fetching suppliers:', err);
      setSuppliers([]);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchSuppliers();
  }, [fetchOrders]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const navigate = useNavigate();

  const openNewOrder = () => {
    navigate('/compres/comandes/new');
  };

  const openEditOrder = (order: PurchaseOrder) => {
    navigate(`/compres/comandes/${order.orderId}/edit`);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingOrder(null);
    setLines([]);
    setSelectedSupplier(null);
  };

  const handleSupplierChange = (supplierId: string) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setSelectedSupplier(supplier || null);
    setEditingOrder(prev => ({
      ...prev,
      supplierId,
      deliveryLocation: supplier?.address ? `${supplier.address}, ${supplier.postalCode || ''} ${supplier.city || ''}` : '',
      paymentMethod: supplier?.paymentTerms || '',
      contactName: '',
      contactPhone: supplier?.phone || '',
    }));
  };

  const addLine = () => {
    setLines(prev => [...prev, { ...emptyLine, lineNumber: prev.length + 1 }]);
  };

  const removeLine = (index: number) => {
    setLines(prev => prev.filter((_, i) => i !== index));
  };

  const updateLine = (index: number, field: keyof PurchaseOrderLine, value: unknown) => {
    setLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Camps en € o Kg editables directament: arrodonits en desar-los a
      // l'estat perquè l'input type="number" no mostri decimals de coma
      // flotant en fer-hi focus.
      if (field === 'unitPrice') updated[index].unitPrice = round(value as number);
      if (field === 'weightKg') updated[index].weightKg = round(value as number, 3);

      // Recalcular lineTotal
      const qty = Number(updated[index].quantity) || 0;
      const price = Number(updated[index].unitPrice) || 0;
      const discount = Number(updated[index].discountPercent) || 0;
      updated[index].lineTotal = round(qty * price * (1 - discount / 100));

      return updated;
    });
  };

  const calculateTotals = () => {
    const subtotal = round(lines.reduce((sum, l) => sum + Number(l.lineTotal || 0), 0));
    const totalWeight = round(lines.reduce((sum, l) => sum + Number(l.weightKg || 0) * Number(l.quantity || 0), 0), 3);
    const taxRate = Number(editingOrder?.taxRate) || 21;
    const taxAmount = round(subtotal * (taxRate / 100));
    const total = round(subtotal + taxAmount);
    return { subtotal, taxAmount, total, totalWeight };
  };

  const handleSave = async () => {
    if (!editingOrder?.supplierId) {
      setError('Has de seleccionar un proveidor');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const totals = calculateTotals();
      const payload = {
        ...editingOrder,
        ...totals,
        items: lines.map((l, idx) => ({
          element: l.element,
          quantity: l.quantity,
          unitType: l.unitType,
          description: l.description,
          supplierCode: l.supplierCode,
          purchasePrice: l.purchasePrice,
          weightKg: l.weightKg,
          unitPrice: l.unitPrice,
          discountPercent: l.discountPercent,
          materialId: l.materialId,
          lineNumber: idx + 1,
        })),
      };

      if (editingOrder.id) {
        await api.put(`/purchase-orders/${editingOrder.id}`, payload);
      } else {
        await api.post('/purchase-orders', payload);
      }

      closeModal();
      fetchOrders();
    } catch (err) {
      console.error('Error saving order:', err);
      setError('Error en guardar la comanda');
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async () => {
    if (!actionConfirm) return;
    const { order, action } = actionConfirm;
    try {
      switch (action) {
        case 'submit':
          await api.post(`/purchase-orders/${order.orderId}/submit`);
          break;
        case 'cancel':
          await api.post(`/purchase-orders/${order.orderId}/cancel`, { reason: 'Cancel·lat per usuari' });
          break;
        case 'delete':
          await api.delete(`/purchase-orders/${order.orderId}`);
          break;
      }
      setActionConfirm(null);
      fetchOrders();
    } catch (err) {
      console.error('Error performing action:', err);
      setError('Error en realitzar la accio');
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'submit': return 'Marcar com enviada';
      case 'cancel': return 'Cancel·lar Comanda';
      case 'delete': return 'Eliminar Comanda';
      default: return action;
    }
  };

  const totals = calculateTotals();
  const paginatedOrders = sortedOrders.slice((page - 1) * limit, page * limit);

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            title="Netejar tots els filtres"
            onClick={() => { setSearch(''); setStatusFilter('ACTIVE'); }}
          >
            <FilterX className="h-4 w-4" />
          </Button>
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Cercar per numero o proveidor..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <ExcelFilterDropdown
            label="Estat"
            options={Object.entries(statusConfig).map(([key, cfg]) => ({ value: key, label: cfg.label }))}
            selected={
              statusFilter === 'all' ? [] :
              statusFilter === 'ACTIVE' ? ACTIVE_STATUSES :
              statusFilter.split(',')
            }
            onApply={(values) => setStatusFilter(values.length === 0 ? 'all' : values.join(','))}
          />
          <Button variant="outline" size="sm" onClick={fetchOrders}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <Button onClick={openNewOrder}>
          <Plus className="mr-2 h-4 w-4" />
          Nova Comanda
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="rounded-xl">
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Comandes a Proveïdors
            </CardTitle>
            <span className="text-sm text-muted-foreground">{total} comandes</span>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : orders.length === 0 ? (
            <div className="flex h-32 flex-col items-center justify-center text-muted-foreground">
              <FileText className="mb-2 h-8 w-8" />
              <span>No s&apos;han trobat comandes</span>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {([
                    { field: 'orderNumber' as const,          label: 'Num. / Comanda',     cls: '' },
                    { field: 'supplierName' as const,         label: 'Proveïdor',           cls: '' },
                    { field: 'projectNumber' as const,        label: 'Expedient',           cls: '' },
                    { field: 'orderDate' as const,            label: 'Data',                cls: '' },
                    { field: 'subtotal' as const,             label: 'Base',                cls: 'text-right' },
                    { field: 'expectedDeliveryDate' as const, label: 'Termini lliurament',  cls: '' },
                    { field: 'status' as const,               label: 'Estat',               cls: 'text-center' },
                  ]).map(({ field, label, cls }) => (
                    <TableHead key={field} className={cls}>
                      <button
                        onClick={() => handleSort(field)}
                        className={`inline-flex items-center gap-0.5 hover:text-foreground transition-colors ${sortField === field ? 'text-blue-600 font-semibold' : 'text-muted-foreground'}`}
                      >
                        {label}<SortIcon field={field} />
                      </button>
                    </TableHead>
                  ))}
                  <TableHead className="text-right">Accions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedOrders.map((order) => (
                  <TableRow key={order.orderId} className="cursor-pointer hover:bg-muted/50" onClick={() => openEditOrder(order)}>
                    <TableCell className="font-mono">
                      <div>{order.orderNumber}</div>
                      {order.otNumber && <div className="text-xs">{order.otNumber}</div>}
                    </TableCell>
                    <TableCell>{order.supplierName || '-'}</TableCell>
                    <TableCell>{order.projectNumber || '-'}</TableCell>
                    <TableCell>{formatDate(order.orderDate)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(order.subtotal)}</TableCell>
                    <TableCell>{order.expectedDeliveryDate ? formatDate(order.expectedDeliveryDate) : '-'}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={statusConfig[order.status]?.color}>
                        {statusConfig[order.status]?.label || order.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => printPDF(order.orderId)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors" title="Imprimir">
                          <Printer size={14} />
                        </button>
                        <button onClick={() => downloadPDF(order.orderId, order.orderNumber)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors" title="Descarregar PDF">
                          <Download size={14} />
                        </button>
                        <button onClick={() => navigate(`/compres/comandes/${order.orderId}`)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors" title="Enviar per correu">
                          <Mail size={14} />
                        </button>
                        <button onClick={() => openEditOrder(order)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors" title="Editar">
                          <Edit size={14} />
                        </button>
                        <button onClick={() => setActionConfirm({ order, action: 'submit' })} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-green-600 transition-colors" title="Enviar">
                          <Send size={14} />
                        </button>
                        <button onClick={() => setActionConfirm({ order, action: 'delete' })} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors" title="Eliminar">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Pagina {page} de {totalPages}</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Modal */}
      {showModal && editingOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] xl:max-w-[1400px] mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-sm font-semibold">
                {editingOrder.id ? `Comanda ${editingOrder.orderNumber}` : 'Nova Comanda'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-slate-100 rounded-md">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-6">
              {/* Header row */}
              <div className="grid grid-cols-12 gap-4">
                {/* Left column - Form fields */}
                <div className="col-span-8 space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="text-sm font-medium">Num. Comanda</label>
                      <Input value={editingOrder.orderNumber || ''} readOnly className="bg-slate-50" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">OT</label>
                      <Input
                        value={editingOrder.otNumber || ''}
                        onChange={(e) => setEditingOrder(prev => ({ ...prev, otNumber: e.target.value }))}
                      />
                    </div>
                    <div className="flex items-end gap-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editingOrder.isServed || false}
                          onChange={(e) => setEditingOrder(prev => ({ ...prev, isServed: e.target.checked }))}
                          className="h-4 w-4"
                        />
                        <span className="text-sm">Servit</span>
                      </label>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Data</label>
                      <Input
                        type="date"
                        value={editingOrder.orderDate?.split('T')[0] || ''}
                        onChange={(e) => setEditingOrder(prev => ({ ...prev, orderDate: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Persona</label>
                      <Input
                        value={editingOrder.persona || ''}
                        onChange={(e) => setEditingOrder(prev => ({ ...prev, persona: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Forma de tramesa</label>
                      <Select
                        value={editingOrder.shippingMethod || ''}
                        onChange={(e) => setEditingOrder(prev => ({ ...prev, shippingMethod: e.target.value }))}
                      >
                        <option value="">Selecciona...</option>
                        {SHIPPING_METHODS.map((method) => (
                          <option key={method.value} value={method.value}>
                            {method.label}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Termini de lliurament</label>
                      <Select
                        value={editingOrder.deliveryTerm || ''}
                        onChange={(e) => setEditingOrder(prev => ({ ...prev, deliveryTerm: e.target.value }))}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Immediat">Immediat</option>
                        <option value="1 setmana">1 setmana</option>
                        <option value="2 setmanes">2 setmanes</option>
                        <option value="3 setmanes">3 setmanes</option>
                        <option value="1 mes">1 mes</option>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Lloc de lliurament</label>
                      <Input
                        value={editingOrder.deliveryLocation || ''}
                        onChange={(e) => setEditingOrder(prev => ({ ...prev, deliveryLocation: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Forma de pagament</label>
                      <Select
                        value={editingOrder.paymentMethod || ''}
                        onChange={(e) => setEditingOrder(prev => ({ ...prev, paymentMethod: e.target.value }))}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Rebut domiciliat a 30 dies">Rebut domiciliat a 30 dies</option>
                        <option value="Rebut domiciliat a 60 dies">Rebut domiciliat a 60 dies</option>
                        <option value="Transferencia">Transferencia</option>
                        <option value="Contado">Contado</option>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Observacions</label>
                      <Input
                        value={editingOrder.observations || ''}
                        onChange={(e) => setEditingOrder(prev => ({ ...prev, observations: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Right column - Supplier info */}
                <div className="col-span-4 space-y-3 border-l pl-4">
                  <div>
                    <label className="text-sm font-medium">Proveïdor *</label>
                    <Select
                      value={editingOrder.supplierId || ''}
                      onChange={(e) => handleSupplierChange(e.target.value)}
                    >
                      <option value="">Selecciona proveidor...</option>
                      {suppliers.map(s => (
                        <option key={s.id} value={s.id}>{s.businessName || s.name}</option>
                      ))}
                    </Select>
                  </div>

                  {selectedSupplier && (
                    <>
                      <div>
                        <span className="text-xs text-muted-foreground">Domicili</span>
                        <p className="text-sm">{selectedSupplier.address || '-'}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Població</span>
                          <p className="text-sm">{selectedSupplier.postalCode} {selectedSupplier.city}</p>
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">NIF</span>
                        <p className="text-sm">{selectedSupplier.nif || '-'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">Telefon / Correu</span>
                        <p className="text-sm">{selectedSupplier.phone} {selectedSupplier.email}</p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">Condicions pagament</span>
                        <p className="text-sm">{selectedSupplier.paymentTerms} {selectedSupplier.bankAccount}</p>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Contacte comanda</label>
                        <Input
                          value={editingOrder.contactName || ''}
                          onChange={(e) => setEditingOrder(prev => ({ ...prev, contactName: e.target.value }))}
                          className="h-7 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Telefon contacte</label>
                        <Input
                          value={editingOrder.contactPhone || ''}
                          onChange={(e) => setEditingOrder(prev => ({ ...prev, contactPhone: e.target.value }))}
                          className="h-7 text-sm"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Lines table */}
              <div className="border rounded-lg">
                <div className="flex items-center justify-between p-3 border-b bg-slate-50">
                  <h3 className="font-medium">Linies de Comanda</h3>
                  <Button size="sm" onClick={addLine}>
                    <Plus className="h-4 w-4 mr-1" /> Afegir linia
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-2 py-2 text-left w-16">Element</th>
                        <th className="px-2 py-2 text-center w-16">Ut.</th>
                        <th className="px-2 py-2 text-center w-16">Tipus</th>
                        <th className="px-2 py-2 text-left">Descripcio</th>
                        <th className="px-2 py-2 text-left w-24">Codi Provei.</th>
                        <th className="px-2 py-2 text-right w-24">Preu Compra</th>
                        <th className="px-2 py-2 text-right w-20">Pes Kg</th>
                        <th className="px-2 py-2 text-right w-24">Preu/Ut</th>
                        <th className="px-2 py-2 text-right w-16">Desc.%</th>
                        <th className="px-2 py-2 text-right w-24">Import</th>
                        <th className="px-2 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50">
                          <td className="px-1 py-1">
                            <Input
                              type="number"
                              value={line.element || 0}
                              onChange={(e) => updateLine(idx, 'element', parseInt(e.target.value) || 0)}
                              className="h-7 text-sm text-center w-14"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <Input
                              type="number"
                              step="0.01"
                              value={line.quantity}
                              onChange={(e) => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)}
                              className="h-7 text-sm text-right w-14"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <Select
                              value={line.unitType}
                              onChange={(e) => updateLine(idx, 'unitType', e.target.value)}
                              className="h-7 text-sm w-14"
                            >
                              <option value="Ut">Ut</option>
                              <option value="Kg">Kg</option>
                              <option value="ml">ml</option>
                              <option value="m2">m2</option>
                              <option value="m3">m3</option>
                              <option value="l">l</option>
                              <option value="H">H</option>
                              <option value="D">D</option>
                            </Select>
                          </td>
                          <td className="px-1 py-1">
                            <Input
                              value={line.description}
                              onChange={(e) => updateLine(idx, 'description', e.target.value)}
                              className="h-7 text-sm"
                              placeholder="Descripcio del material..."
                            />
                          </td>
                          <td className="px-1 py-1">
                            <Input
                              value={line.supplierCode || ''}
                              onChange={(e) => updateLine(idx, 'supplierCode', e.target.value)}
                              className="h-7 text-sm"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <Input
                              type="number"
                              step="0.01"
                              value={line.purchasePrice}
                              onChange={(e) => updateLine(idx, 'purchasePrice', parseFloat(e.target.value) || 0)}
                              className="h-7 text-sm text-right"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <Input
                              type="number"
                              step="0.01"
                              value={line.weightKg}
                              onChange={(e) => updateLine(idx, 'weightKg', parseFloat(e.target.value) || 0)}
                              className="h-7 text-sm text-right"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <Input
                              type="number"
                              step="0.01"
                              value={line.unitPrice}
                              onChange={(e) => updateLine(idx, 'unitPrice', parseFloat(e.target.value) || 0)}
                              className="h-7 text-sm text-right"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <Input
                              type="number"
                              step="0.1"
                              value={line.discountPercent}
                              onChange={(e) => updateLine(idx, 'discountPercent', parseFloat(e.target.value) || 0)}
                              className="h-7 text-sm text-right w-14"
                            />
                          </td>
                          <td className="px-1 py-1 text-right font-medium">
                            {Number(line.lineTotal || 0).toFixed(2)}
                          </td>
                          <td className="px-1 py-1">
                            <button onClick={() => removeLine(idx)} className="p-1 hover:bg-red-100 rounded text-red-600">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="p-3 border-t bg-slate-50 flex justify-end gap-8">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Total pes:</span>
                    <span className="ml-2 font-medium">{totals.totalWeight.toFixed(2)} Kg</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Base imposable:</span>
                    <span className="ml-2 font-medium">{totals.subtotal.toFixed(2)} &euro;</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">IVA 21%:</span>
                    <span className="ml-2 font-medium">{totals.taxAmount.toFixed(2)} &euro;</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground font-medium">Total Comanda:</span>
                    <span className="ml-2 font-bold text-sm">{totals.total.toFixed(2)} &euro;</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={closeModal}>Cancel·lar</Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-1" />
                {saving ? 'Desant...' : 'Desa'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Action Confirmation */}
      {actionConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md">
            <h3 className="text-sm font-semibold mb-4">Confirmar accio</h3>
            <p className="mb-6">
              Estas segur que vols {getActionLabel(actionConfirm.action)} la comanda{' '}
              <strong>{actionConfirm.order.orderNumber}</strong>?
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setActionConfirm(null)}>Cancel·lar</Button>
              <Button
                variant={actionConfirm.action === 'cancel' || actionConfirm.action === 'delete' ? 'destructive' : 'default'}
                onClick={handleAction}
              >
                Confirmar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
